import React from "react";
import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Loader2, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";

interface Slide {
  title: string;
  bullets: string[];
  image_prompt?: string;
}

interface PitchDeck {
  id: string;
  title: string;
  audience: string;
  tone: string;
  slide_count: number;
  outline: Slide[];
  created_by: string;
}

interface Profile {
  full_name: string;
  avatar_url?: string;
}

const PublicDeckViewer = () => {
  const { token } = useParams<{ token: string }>();
  const [deck, setDeck] = useState<PitchDeck | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [currentSlide, setCurrentSlide] = useState(0);
  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    if (token) {
      fetchDeck();
    }
  }, [token]);

  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") {
        prevSlide();
      } else if (e.key === "ArrowRight") {
        nextSlide();
      }
    };

    window.addEventListener("keydown", handleKeyPress);
    return () => window.removeEventListener("keydown", handleKeyPress);
  }, [currentSlide, deck]);

  const fetchDeck = async () => {
    try {
      setLoading(true);

      // Fetch deck by public token
      const { data: deckData, error: deckError } = await supabase
        .from("pitch_decks")
        .select("*")
        .eq("public_token", token)
        .eq("is_public", true)
        .single();

      if (deckError) throw deckError;
      if (!deckData) throw new Error("Deck not found");

      setDeck(deckData as unknown as PitchDeck);

      // Fetch creator profile
      const { data: profileData } = await supabase
        .from("profiles")
        .select("full_name, avatar_url")
        .eq("id", deckData.created_by)
        .single();

      if (profileData) {
        setProfile(profileData);
      }

      // Track view via edge function
      const urlParams = new URLSearchParams(window.location.search);
      const outreachId = urlParams.get("o");

      await supabase.functions.invoke("track-deck-view", {
        body: {
          token,
          userAgent: navigator.userAgent,
          referrer: document.referrer || null,
          outreachId: outreachId || null,
        },
      });
    } catch (err: any) {
      console.error("Error fetching deck:", err);
      toast({
        title: "Error",
        description: "Deck not public or removed",
        variant: "destructive",
      });
      navigate("/");
    } finally {
      setLoading(false);
    }
  };

  const nextSlide = () => {
    if (deck && currentSlide < deck.outline.length - 1) {
      setCurrentSlide(currentSlide + 1);
    }
  };

  const prevSlide = () => {
    if (currentSlide > 0) {
      setCurrentSlide(currentSlide - 1);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/20 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!deck) return null;

  const progress = deck.outline.length > 0 ? ((currentSlide + 1) / deck.outline.length) * 100 : 0;
  const slide = deck.outline[currentSlide];

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/20">
      {/* Header */}
      <div className="border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold">{deck.title}</h1>
              <p className="text-sm text-muted-foreground mt-1">
                by {profile?.full_name || "Anonymous"}
              </p>
            </div>
            <Link to="/">
              <Button variant="outline">Start Your Own Idea</Button>
            </Link>
          </div>
        </div>
      </div>

      {/* Slide Viewer */}
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-muted-foreground">
              Slide {currentSlide + 1} of {deck.outline.length}
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={prevSlide}
                disabled={currentSlide === 0}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={nextSlide}
                disabled={currentSlide === deck.outline.length - 1}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
          <Progress value={progress} className="h-2" />
        </div>

        <Card className="min-h-[400px]">
          <CardHeader>
            <CardTitle className="text-3xl">{slide.title}</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-3 list-disc list-inside text-lg">
              {slide.bullets.map((bullet, idx) => (
                <li key={idx} className="text-muted-foreground">
                  {bullet}
                </li>
              ))}
            </ul>
            {slide.image_prompt && (
              <p className="text-xs text-muted-foreground mt-6 italic">
                Image suggestion: {slide.image_prompt}
              </p>
            )}
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="mt-8 text-center text-sm text-muted-foreground">
          <p>Created with DayOne • Turn your ideas into reality</p>
        </div>
      </div>
    </div>
  );
};

export default PublicDeckViewer;
